import { Crop, FlipHorizontal2, FlipVertical2, Grid3X3, Maximize2, Minus, Plus, Redo2, RotateCcw, RotateCw, Undo2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export type EditorRecipe = {
  rotation: number;
  flipX: boolean;
  flipY: boolean;
  zoom: number;
  crop: { x: number; y: number; width: number; height: number; rotation?: number };
  aspect: string;
};

type CropRect = EditorRecipe["crop"];
type ResizeHandle = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
type CropInteraction = {
  mode: "move" | "resize" | "rotate";
  handle?: ResizeHandle;
  startX: number;
  startY: number;
  origin: CropRect;
  latest: CropRect;
  latestAspect: string;
  startAngle?: number;
  originRotation?: number;
};

const initialRecipe: EditorRecipe = { rotation: 0, flipX: false, flipY: false, zoom: 1, crop: { x: .2, y: .2, width: .6, height: .6, rotation: 0 }, aspect: "1:1" };
const MIN_CROP_PX = 1;

export function editorBackingSize(cssWidth: number, cssHeight: number, devicePixelRatio = 1) {
  const width = Math.max(1, cssWidth); const height = Math.max(1, cssHeight);
  const qualityScale = Math.min(1080 / Math.min(width, height), 2048 / Math.max(width, height));
  const scale = Math.max(devicePixelRatio, qualityScale);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function numericAspect(aspect: string) {
  const [width, height] = aspect.split(":").map(Number);
  return width > 0 && height > 0 ? width / height : 1;
}

export function fitCropToAspect(crop: CropRect, aspect: string, frameWidth: number, frameHeight: number): CropRect {
  if (aspect === "free") return crop;
  if (frameWidth <= 0 || frameHeight <= 0) return crop;
  const normalizedRatio = numericAspect(aspect) * frameHeight / frameWidth;
  let width = crop.width; let height = crop.height;
  if (width / height > normalizedRatio) width = height * normalizedRatio;
  else height = width / normalizedRatio;
  const centerX = crop.x + crop.width / 2; const centerY = crop.y + crop.height / 2;
  return {
    x: Math.max(0, Math.min(1 - width, centerX - width / 2)),
    y: Math.max(0, Math.min(1 - height, centerY - height / 2)),
    width,
    height,
    rotation: crop.rotation || 0,
  };
}

function drawImage(canvas: HTMLCanvasElement, image: HTMLImageElement, recipe: EditorRecipe) {
  const context = canvas.getContext("2d");
  if (!context) return;
  const size = editorBackingSize(canvas.clientWidth, canvas.clientHeight, window.devicePixelRatio);
  const width = size.width; const height = size.height;
  if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#090a0c";
  context.fillRect(0, 0, width, height);
  const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight) * recipe.zoom;
  context.save();
  context.translate(width / 2, height / 2);
  context.rotate((recipe.rotation * Math.PI) / 180);
  context.scale(recipe.flipX ? -fit : fit, recipe.flipY ? -fit : fit);
  context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
  context.restore();
}

export async function exportEditorCanvases(source: HTMLCanvasElement, crop: EditorRecipe["crop"], aspect = "1:1"): Promise<{ full: Blob; clue: Blob }> {
  const fullCanvas = document.createElement("canvas");
  const scale = Math.min(1, 1920 / Math.max(source.width, source.height));
  fullCanvas.width = Math.max(1, Math.round(source.width * scale));
  fullCanvas.height = Math.max(1, Math.round(source.height * scale));
  fullCanvas.getContext("2d")!.drawImage(source, 0, 0, fullCanvas.width, fullCanvas.height);
  const clueCanvas = document.createElement("canvas");
  const ratio = aspect === "free" ? (crop.width * source.width) / Math.max(1, crop.height * source.height) : numericAspect(aspect);
  clueCanvas.width = ratio >= 1 ? 1080 : Math.max(1, Math.round(1080 * ratio));
  clueCanvas.height = ratio >= 1 ? Math.max(1, Math.round(1080 / ratio)) : 1080;
  const context = clueCanvas.getContext("2d")!;
  context.fillStyle = "#111216"; context.fillRect(0, 0, clueCanvas.width, clueCanvas.height);
  const sw = crop.width * source.width, sh = crop.height * source.height;
  const centerX = (crop.x + crop.width / 2) * source.width, centerY = (crop.y + crop.height / 2) * source.height;
  context.save();
  context.translate(clueCanvas.width / 2, clueCanvas.height / 2);
  context.scale(clueCanvas.width / Math.max(1, sw), clueCanvas.height / Math.max(1, sh));
  context.rotate(-((crop.rotation || 0) * Math.PI) / 180);
  context.translate(-centerX, -centerY);
  context.drawImage(source, 0, 0);
  context.restore();
  const toBlob = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Canvas tidak dapat diekspor.")), "image/jpeg", .9));
  return { full: await toBlob(fullCanvas), clue: await toBlob(clueCanvas) };
}

export function PhotoEditor({ file, recipe, onChange, canvasRef }: { file: File; recipe: EditorRecipe; onChange: (recipe: EditorRecipe) => void; canvasRef: React.RefObject<HTMLCanvasElement | null> }) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [history, setHistory] = useState<EditorRecipe[]>([initialRecipe]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const frameRef = useRef<HTMLDivElement>(null);
  const interactionRef = useRef<CropInteraction | null>(null);
  const appliedAspectRef = useRef<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const next = new Image();
    next.onload = () => { appliedAspectRef.current = null; setImage(next); };
    next.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const redraw = useCallback(() => { if (image && canvasRef.current) drawImage(canvasRef.current, image, recipe); }, [canvasRef, image, recipe.flipX, recipe.flipY, recipe.rotation, recipe.zoom]);
  useEffect(() => {
    redraw();
    const observer = new ResizeObserver(redraw);
    if (frameRef.current) observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [redraw]);

  useEffect(() => {
    if (!image || !frameRef.current) return;
    const key = `${image.src}:${recipe.aspect}`;
    if (appliedAspectRef.current === key) return;
    appliedAspectRef.current = key;
    const bounds = frameRef.current.getBoundingClientRect();
    const crop = fitCropToAspect(recipe.crop, recipe.aspect, bounds.width, bounds.height);
    onChange({ ...recipe, crop });
  }, [image, onChange, recipe.aspect]);

  const commit = (next: EditorRecipe) => {
    const nextHistory = history.slice(0, historyIndex + 1).concat(next);
    setHistory(nextHistory); setHistoryIndex(nextHistory.length - 1); onChange(next);
  };
  const patch = (value: Partial<EditorRecipe>) => commit({ ...recipe, ...value });
  const undo = () => { if (historyIndex > 0) { const index = historyIndex - 1; setHistoryIndex(index); onChange(history[index]); } };
  const redo = () => { if (historyIndex < history.length - 1) { const index = historyIndex + 1; setHistoryIndex(index); onChange(history[index]); } };

  const pointerDown = (event: React.PointerEvent) => {
    if (!frameRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    interactionRef.current = { mode: "move", startX: event.clientX, startY: event.clientY, origin: recipe.crop, latest: recipe.crop, latestAspect: recipe.aspect };
  };
  const resizeDown = (handle: ResizeHandle) => (event: React.PointerEvent) => {
    event.preventDefault(); event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    interactionRef.current = { mode: "resize", handle, startX: event.clientX, startY: event.clientY, origin: recipe.crop, latest: recipe.crop, latestAspect: recipe.aspect };
  };
  const rotateDown = (event: React.PointerEvent) => {
    if (!frameRef.current) return;
    event.preventDefault(); event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = frameRef.current.getBoundingClientRect();
    const centerX = bounds.left + (recipe.crop.x + recipe.crop.width / 2) * bounds.width;
    const centerY = bounds.top + (recipe.crop.y + recipe.crop.height / 2) * bounds.height;
    interactionRef.current = { mode: "rotate", startX: event.clientX, startY: event.clientY, startAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX), originRotation: recipe.crop.rotation || 0, origin: recipe.crop, latest: recipe.crop, latestAspect: recipe.aspect };
  };
  const pointerMove = (event: React.PointerEvent) => {
    const interaction = interactionRef.current;
    if (!interaction || !frameRef.current) return;
    const bounds = frameRef.current.getBoundingClientRect();
    let crop: CropRect;
    if (interaction.mode === "rotate") {
      const centerX = bounds.left + (interaction.origin.x + interaction.origin.width / 2) * bounds.width;
      const centerY = bounds.top + (interaction.origin.y + interaction.origin.height / 2) * bounds.height;
      const angle = Math.atan2(event.clientY - centerY, event.clientX - centerX);
      let rotation = (interaction.originRotation || 0) + (angle - (interaction.startAngle || 0)) * 180 / Math.PI;
      rotation = ((rotation + 180) % 360 + 360) % 360 - 180;
      if (event.shiftKey) rotation = Math.round(rotation / 15) * 15;
      crop = { ...interaction.origin, rotation };
    } else if (interaction.mode === "move") {
      const x = Math.max(0, Math.min(1 - interaction.origin.width, interaction.origin.x + (event.clientX - interaction.startX) / bounds.width));
      const y = Math.max(0, Math.min(1 - interaction.origin.height, interaction.origin.y + (event.clientY - interaction.startY) / bounds.height));
      crop = { ...interaction.origin, x, y };
    } else {
      const handle = interaction.handle!; const west = handle.includes("w"); const east = handle.includes("e"); const north = handle.includes("n"); const south = handle.includes("s");
      const originLeft = interaction.origin.x * bounds.width; const originTop = interaction.origin.y * bounds.height;
      const originWidth = interaction.origin.width * bounds.width; const originHeight = interaction.origin.height * bounds.height;
      const originRight = originLeft + originWidth; const originBottom = originTop + originHeight;
      const originCenterX = originLeft + originWidth / 2; const originCenterY = originTop + originHeight / 2;
      const worldX = event.clientX - bounds.left; const worldY = event.clientY - bounds.top;
      const radians = ((interaction.origin.rotation || 0) * Math.PI) / 180; const cosine = Math.cos(radians); const sine = Math.sin(radians);
      const deltaX = worldX - originCenterX; const deltaY = worldY - originCenterY;
      const pointerX = deltaX * cosine + deltaY * sine; const pointerY = -deltaX * sine + deltaY * cosine;
      const localLeft = -originWidth / 2; const localRight = originWidth / 2; const localTop = -originHeight / 2; const localBottom = originHeight / 2;
      const isCorner = (west || east) && (north || south);
      if (isCorner && recipe.aspect !== "free") {
        const anchorX = west ? localRight : localLeft; const anchorY = north ? localBottom : localTop;
        const distanceX = Math.max(0, (pointerX - anchorX) * (west ? -1 : 1));
        const distanceY = Math.max(0, (pointerY - anchorY) * (north ? -1 : 1));
        const changeX = Math.abs(distanceX - originWidth) / Math.max(1, originWidth);
        const changeY = Math.abs(distanceY - originHeight) / Math.max(1, originHeight);
        const requestedScale = changeX >= changeY ? distanceX / originWidth : distanceY / originHeight;
        const maxScale = Math.min(bounds.width / originWidth, bounds.height / originHeight);
        const minScale = Math.min(maxScale, Math.max(MIN_CROP_PX / originWidth, MIN_CROP_PX / originHeight));
        const scale = Math.max(minScale, Math.min(maxScale, requestedScale));
        const width = originWidth * scale; const height = originHeight * scale;
        const left = west ? anchorX - width : anchorX; const top = north ? anchorY - height : anchorY;
        const localCenterX = left + width / 2; const localCenterY = top + height / 2;
        const centerX = originCenterX + localCenterX * cosine - localCenterY * sine; const centerY = originCenterY + localCenterX * sine + localCenterY * cosine;
        const normalizedWidth = width / bounds.width; const normalizedHeight = height / bounds.height;
        crop = { x: Math.max(0, Math.min(1 - normalizedWidth, centerX / bounds.width - normalizedWidth / 2)), y: Math.max(0, Math.min(1 - normalizedHeight, centerY / bounds.height - normalizedHeight / 2)), width: normalizedWidth, height: normalizedHeight, rotation: interaction.origin.rotation || 0 };
      } else {
        const left = west ? Math.min(localRight - MIN_CROP_PX, pointerX) : localLeft;
        const right = east ? Math.max(localLeft + MIN_CROP_PX, pointerX) : localRight;
        const top = north ? Math.min(localBottom - MIN_CROP_PX, pointerY) : localTop;
        const bottom = south ? Math.max(localTop + MIN_CROP_PX, pointerY) : localBottom;
        const width = Math.min(bounds.width, right - left); const height = Math.min(bounds.height, bottom - top);
        const localCenterX = (left + right) / 2; const localCenterY = (top + bottom) / 2;
        const centerX = originCenterX + localCenterX * cosine - localCenterY * sine; const centerY = originCenterY + localCenterX * sine + localCenterY * cosine;
        const normalizedWidth = width / bounds.width; const normalizedHeight = height / bounds.height;
        crop = { x: Math.max(0, Math.min(1 - normalizedWidth, centerX / bounds.width - normalizedWidth / 2)), y: Math.max(0, Math.min(1 - normalizedHeight, centerY / bounds.height - normalizedHeight / 2)), width: normalizedWidth, height: normalizedHeight, rotation: interaction.origin.rotation || 0 };
        interaction.latestAspect = "free";
      }
    }
    interaction.latest = crop;
    onChange({ ...recipe, crop, aspect: interaction.latestAspect });
  };
  const pointerUp = () => {
    const interaction = interactionRef.current;
    if (!interaction) return;
    interactionRef.current = null;
    commit({ ...recipe, crop: interaction.latest, aspect: interaction.latestAspect });
  };

  return (
    <div className="photo-editor">
      <div className="editor-toolbar">
        <div><button onClick={undo} disabled={historyIndex === 0} title="Undo"><Undo2 size={16} /></button><button onClick={redo} disabled={historyIndex === history.length - 1} title="Redo"><Redo2 size={16} /></button></div>
        <span />
        <div><button onClick={() => patch({ rotation: recipe.rotation - 90 })} title="Putar kiri"><RotateCcw size={16} /></button><button onClick={() => patch({ rotation: recipe.rotation + 90 })} title="Putar kanan"><RotateCw size={16} /></button><button className={recipe.flipX ? "active" : ""} onClick={() => patch({ flipX: !recipe.flipX })} title="Balik horizontal"><FlipHorizontal2 size={16} /></button><button className={recipe.flipY ? "active" : ""} onClick={() => patch({ flipY: !recipe.flipY })} title="Balik vertikal"><FlipVertical2 size={16} /></button></div>
        <span />
        <div><button onClick={() => patch({ zoom: Math.max(.5, recipe.zoom - .1) })}><Minus size={16} /></button><em>{Math.round(recipe.zoom * 100)}%</em><button onClick={() => patch({ zoom: Math.min(3, recipe.zoom + .1) })}><Plus size={16} /></button></div>
        <span className="toolbar-flex" />
        <button onClick={() => commit(initialRecipe)}><Maximize2 size={15} /> Fit</button>
      </div>
      <div className="editor-frame" ref={frameRef}>
        <canvas ref={canvasRef} />
        <div className="crop-shade" />
        <div className="crop-selection" style={{ left: `${recipe.crop.x * 100}%`, top: `${recipe.crop.y * 100}%`, width: `${recipe.crop.width * 100}%`, height: `${recipe.crop.height * 100}%`, transform: `rotate(${recipe.crop.rotation || 0}deg)` }} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp}>
          <button className="rotate-handle" aria-label="Putar area clue" title="Putar area clue (tahan Shift untuk kelipatan 15°)" onPointerDown={rotateDown}><RotateCw size={12} /></button>
          <button className="resize-handle nw" aria-label="Ubah ukuran dari sudut kiri atas" onPointerDown={resizeDown("nw")} />
          <button className="resize-handle n" aria-label="Ubah tinggi dari sisi atas" onPointerDown={resizeDown("n")} />
          <button className="resize-handle ne" aria-label="Ubah ukuran dari sudut kanan atas" onPointerDown={resizeDown("ne")} />
          <button className="resize-handle e" aria-label="Ubah lebar dari sisi kanan" onPointerDown={resizeDown("e")} />
          <button className="resize-handle s" aria-label="Ubah tinggi dari sisi bawah" onPointerDown={resizeDown("s")} />
          <button className="resize-handle sw" aria-label="Ubah ukuran dari sudut kiri bawah" onPointerDown={resizeDown("sw")} />
          <button className="resize-handle w" aria-label="Ubah lebar dari sisi kiri" onPointerDown={resizeDown("w")} />
          <button className="resize-handle se" aria-label="Ubah ukuran dari sudut kanan bawah" onPointerDown={resizeDown("se")} />
          <span className="crop-label"><Crop size={12} /> Area clue</span>
          <div className="third-grid"><i /><i /><i /><i /></div>
        </div>
      </div>
      <div className="editor-bottom-bar">
        <span><Grid3X3 size={14} /> Resize dan putar area secara bebas</span>
        <label>Foto <input type="range" min="-180" max="180" value={recipe.rotation} onChange={(event) => onChange({ ...recipe, rotation: Number(event.target.value) })} onPointerUp={() => commit(recipe)} /><strong>{recipe.rotation}°</strong></label>
        <label>Area <input type="range" min="-180" max="180" value={recipe.crop.rotation || 0} onChange={(event) => onChange({ ...recipe, crop: { ...recipe.crop, rotation: Number(event.target.value) } })} onPointerUp={() => commit(recipe)} /><strong>{Math.round(recipe.crop.rotation || 0)}°</strong></label>
      </div>
    </div>
  );
}
